import { HttpClient } from '../shared/api/HttpClient';
import type { SchedulerConfiguration } from '../scheduler/types';
import type { CuePoints, DspProfile, ItemType, MediaAsset, StationSummary, TrackStationSettings, TrackSummary, TransitionProfile, UploadResponse } from './types';

interface Page<T> { items: T[]; nextCursor: string | null }
interface TransitionPage { stationId: string; items: TransitionProfile[] }

export class SegueApi {
  readonly #http: HttpClient;

  public constructor(http = new HttpClient({ readCsrfToken: readCsrfCookie })) {
    this.#http = http;
  }

  public async stations(): Promise<StationSummary[]> {
    return (await this.#http.request<Page<StationSummary>>('/stations?limit=100')).items;
  }

  public async tracks(): Promise<TrackSummary[]> {
    const tracks: TrackSummary[] = [];
    let cursor: string | null = null;
    do {
      const page: Page<TrackSummary> = await this.#http.request(`/tracks?limit=100${cursor === null ? '' : `&cursor=${encodeURIComponent(cursor)}`}`);
      tracks.push(...page.items);
      cursor = page.nextCursor;
    } while (cursor !== null);
    return tracks;
  }

  public schedulerConfiguration(stationId: string): Promise<SchedulerConfiguration> {
    return this.#http.request(`/stations/${stationId}/scheduler-configuration`);
  }

  public stationSettings(trackId: string, stationId: string): Promise<TrackStationSettings> {
    return this.#http.request(`/tracks/${trackId}/stations/${stationId}`);
  }

  public saveStationSettings(trackId: string, stationId: string, current: TrackStationSettings | null, rotationCategoryId: string | null, enabled: boolean): Promise<TrackStationSettings> {
    return this.#http.request(`/tracks/${trackId}/stations/${stationId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(current === null ? { 'If-None-Match': '*' } : { 'If-Match': `"${current.revision}"` }),
      },
      body: JSON.stringify({
        rotationCategoryId,
        transitionProfileId: current?.transitionProfileId ?? null,
        enabled,
        priority: current?.priority ?? 0,
        weight: current?.weight ?? 1,
        minTrackSeparationSeconds: current?.minTrackSeparationSeconds ?? null,
        artistSeparationSeconds: current?.artistSeparationSeconds ?? null,
        allowedDays: current?.allowedDays ?? null,
        allowedHours: current?.allowedHours ?? null,
        validFrom: current?.validFrom ?? null,
        validUntil: current?.validUntil ?? null,
      }),
    });
  }

  public upload(file: File): Promise<UploadResponse> {
    const body = new FormData();
    body.set('file', file);
    return this.#http.request('/uploads', { method: 'POST', body });
  }

  public mediaAsset(assetId: string): Promise<MediaAsset> {
    return this.#http.request(`/media-assets/${assetId}`);
  }

  public saveMetadata(track: TrackSummary): Promise<TrackSummary> {
    const payload = {
      artist: track.artist,
      title: track.title,
      album: track.album,
      genre: track.genre,
      subgenre: track.subgenre,
      language: track.language,
      mood: track.mood,
      energy: track.energy,
      bpm: track.bpm,
      vocal: track.vocal,
      explicit: track.explicit,
      source: track.source,
      notes: track.notes,
    };
    return this.#http.request(`/tracks/${track.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'If-Match': `"${track.revision}"` },
      body: JSON.stringify(payload),
    });
  }

  public async profiles(stationId: string): Promise<TransitionProfile[]> {
    return (await this.#http.request<TransitionPage>(`/stations/${stationId}/transition-profiles`)).items;
  }

  public dsp(stationId: string): Promise<DspProfile> {
    return this.#http.request(`/stations/${stationId}/dsp-profile`);
  }

  public saveCuePoints(trackId: string, cuePoints: CuePoints): Promise<CuePoints> {
    const { revision, ...payload } = cuePoints;
    return this.#http.request(`/tracks/${trackId}/cue-points`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'If-Match': `"${revision}"` },
      body: JSON.stringify(payload),
    });
  }

  public saveItemType(trackId: string, itemType: ItemType, revision: number): Promise<TrackSummary> {
    return this.#http.request(`/tracks/${trackId}/item-type`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'If-Match': `"${revision}"` },
      body: JSON.stringify({ itemType }),
    });
  }

  public saveProfile(stationId: string, profile: TransitionProfile): Promise<TransitionProfile> {
    const payload = {
      name: profile.name,
      fromType: profile.fromType,
      toType: profile.toType,
      mode: profile.mode,
      settings: profile.settings,
    };
    if (profile.system) {
      return this.#http.request(`/stations/${stationId}/transition-profiles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: crypto.randomUUID(), ...payload, name: `${profile.name} — станція` }),
      });
    }
    return this.#http.request(`/stations/${stationId}/transition-profiles/${profile.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'If-Match': `"${profile.revision}"` },
      body: JSON.stringify(payload),
    });
  }

  public saveDsp(profile: DspProfile): Promise<DspProfile> {
    const { id: _id, stationId, revision, ...payload } = profile;
    return this.#http.request(`/stations/${stationId}/dsp-profile`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'If-Match': `"${revision}"` },
      body: JSON.stringify(payload),
    });
  }

  public audioUrl(trackId: string): string {
    return `/api/v1/tracks/${trackId}/audio`;
  }
}

function readCsrfCookie(): string | null {
  const prefix = '__Host-radio_csrf=';
  const value = document.cookie.split(';').map((part) => part.trim()).find((part) => part.startsWith(prefix));
  return value === undefined ? null : decodeURIComponent(value.slice(prefix.length));
}
